import { useEffect, useState, type RefObject } from 'react';

import { readCssDurationMs } from '../../../../utils/readCssDurationMs';

const CONDENSED_CLASS = 'eds-side-nav--scroll--condensed';

/**
 * Tracks whether an element's content overflows it vertically. Used to drop the
 * rail's item labels once there is no room left to scroll them comfortably,
 * trading the label for vertical space instead.
 *
 * `contentKey` changes whenever what the element holds changes, such as the
 * entry counts. The element's own box does not resize when its content does (it
 * is clipped by `overflow-y: auto` inside a fixed-height column), so a content
 * change needs the effect to run again.
 */
export function useHasVerticalOverflow(
  ref: RefObject<HTMLElement | null>,
  isRail: boolean,
  contentKey: string,
) {
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const checkOverflow = () => {
      const wasCondensed = element.classList.contains(CONDENSED_CLASS);
      if (wasCondensed) {
        element.classList.remove(CONDENSED_CLASS);
      }
      const nextHasOverflow = element.scrollHeight > element.clientHeight;
      if (wasCondensed) {
        element.classList.add(CONDENSED_CLASS);
      }
      setHasOverflow(nextHasOverflow);
    };
    let rafId: number | undefined;
    const scheduleCheck = () => {
      if (rafId !== undefined) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        checkOverflow();
      });
    };

    // The rail's own width animates on collapse and expand, which reflows the
    // labels this measures, so the first check waits for it to settle.
    const timeout = setTimeout(
      checkOverflow,
      readCssDurationMs(element, '--_collapse-duration'),
    );
    const resizeObserver = new ResizeObserver(scheduleCheck);
    resizeObserver.observe(element, { box: 'border-box' });
    return () => {
      clearTimeout(timeout);
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [contentKey, isRail, ref]);

  return hasOverflow;
}
